import type { Environment } from "@backchannel/tax-contracts";
import { SESSION_REPLAY_HEADER } from "@backchannel/tax-contracts";
import {
  gatewayDeadlineError,
  networkError,
  parseRetryAfter,
  toTaxApiError,
  unexpectedResponseError,
} from "./errors";

export type QueryValue = string | number | boolean | string[] | null | undefined;

const REPLAY_EXEMPT_PATHS = new Set(["/v1/session-replay/config", "/v1/session-replays/chunks"]);

function isReplayExemptPath(path: string): boolean {
  const pathOnly = path.split("?")[0] ?? path;
  return REPLAY_EXEMPT_PATHS.has(pathOnly);
}

export type ApiRequest = {
  method: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  path: string;
  /** Explicit tenant selector for principals scoped to more than one org. */
  organizationId?: string;
  query?: Record<string, QueryValue>;
  body?: unknown;
  signal?: AbortSignal;
  /** Response decoder selected by the API consumer. JSON remains the default. */
  responseType?: "json" | "arrayBuffer";
  /** Optional request-level override for latency-sensitive reads. */
  timeoutMs?: number;
  /** Required by the API for every non-idempotent mutation. */
  idempotencyKey?: string;
  /** Numeric optimistic-concurrency revision, serialized as an HTTP ETag. */
  ifMatch?: number;
  /** Browser `fetch` keepalive (session-replay flush on pagehide). */
  keepalive?: boolean;
  /** Closed, purpose-specific version opt-in; never arbitrary caller headers. */
  operationResearchVersion?: "1.0.0";
};

export type ApiResult = {
  status: number;
  body: unknown;
};

/**
 * The single seam between the console and the outside world. Every screen goes
 * through `TaxApiClient`, which goes through a `Transport`. Tests and the demo
 * mode swap the transport; nothing else changes.
 */
export type Transport = (request: ApiRequest) => Promise<ApiResult>;

export function buildQueryString(query: Record<string, QueryValue> | undefined): string {
  if (!query) return "";
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === "") continue;
    if (Array.isArray(value)) {
      if (value.length === 0) continue;
      params.set(key, value.join(","));
      continue;
    }
    params.set(key, String(value));
  }
  const serialized = params.toString();
  return serialized ? `?${serialized}` : "";
}

export type HttpTransportOptions = {
  baseUrl: string;
  environment: Environment;
  /**
   * Maximum time allowed for the Tax API request, including response-body
   * consumption. A bounded request gives query consumers a retryable result
   * instead of leaving the workspace in an infinite loading state.
   */
  timeoutMs?: number;
  /**
   * Resolves the caller's own platform access token. The browser does not
   * attach integration keys; if this returns null the management request is
   * sent unauthenticated and the API answers with `UNAUTHENTICATED`.
   */
  getIdToken: () => Promise<string | null> | string | null;
  /**
   * Called when the gateway rejects a request with 401. A stale/revoked token
   * must transition the auth provider back to its in-app sign-in boundary
   * instead of leaving the authenticated shell retrying the same token.
   */
  onUnauthenticated?: () => void;
  /**
   * Tab-scoped session-replay id. Sent as `x-tax-session-id` on every Tax API
   * request except the replay config and chunk ingest routes.
   */
  getReplaySessionId?: () => string | null;
  /**
   * Called when the request fails before an HTTP answer (offline, timeout,
   * gateway deadline). Not called for aborts or the session-replay routes.
   */
  onNetworkFailure?: (info: { requestId: string; method: string; path: string }) => void;
  fetchImpl?: typeof fetch;
  newId?: () => string;
  /** Milliseconds clock; tests only. */
  now?: () => number;
};

export const DEFAULT_HTTP_TIMEOUT_MS = 30_000;

/**
 * A network error after this long is the API Gateway's deadline (15 s by default, 60 s on the
 * calculator's heavy routes) answered without CORS headers, not a dropped connection.
 */
export const GATEWAY_DEADLINE_HINT_MS = 14_000;

export function newCorrelationId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `id-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
}

export function createHttpTransport(options: HttpTransportOptions): Transport {
  const fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
  const newId = options.newId ?? newCorrelationId;
  const now = options.now ?? (() => Date.now());
  const timeoutMs = options.timeoutMs ?? DEFAULT_HTTP_TIMEOUT_MS;

  if (!Number.isFinite(timeoutMs) || timeoutMs <= 0) {
    throw new RangeError("HTTP transport timeoutMs must be a positive finite number");
  }

  return async function httpTransport(request) {
    if (request.signal?.aborted) {
      throw abortError(request.signal.reason);
    }

    const token = await options.getIdToken();
    // The token resolver is deliberately kept outside the request timeout: it
    // may refresh platform credentials through its own provider flow. Do not
    // start a Tax API timer until there is a request to abort.
    if (request.signal?.aborted) {
      throw abortError(request.signal.reason);
    }

    const requestId = newId();
    const requestTimeoutMs = request.timeoutMs ?? timeoutMs;
    if (!Number.isFinite(requestTimeoutMs) || requestTimeoutMs <= 0) {
      throw new RangeError("HTTP request timeoutMs must be a positive finite number");
    }

    const headers: Record<string, string> = {
      accept:
        request.responseType === "arrayBuffer" ? "application/octet-stream" : "application/json",
      "x-request-id": requestId,
      "x-correlation-id": requestId,
      // Asserted, never trusted: the deployed service configuration decides.
      "x-tax-environment": options.environment,
    };

    if (token) {
      headers.authorization = `Bearer ${token}`;
    }
    if (request.organizationId) {
      headers["x-organization-id"] = request.organizationId;
    }
    if (request.idempotencyKey) {
      headers["idempotency-key"] = request.idempotencyKey;
    }
    if (request.ifMatch !== undefined) {
      headers["if-match"] = `"${request.ifMatch}"`;
    }
    if (request.operationResearchVersion)
      headers["x-tax-operation-research"] = request.operationResearchVersion;
    if (request.body !== undefined) {
      headers["content-type"] = "application/json";
    }
    if (!isReplayExemptPath(request.path) && options.getReplaySessionId) {
      try {
        const replaySessionId = options.getReplaySessionId();
        if (replaySessionId) {
          headers[SESSION_REPLAY_HEADER.toLowerCase()] = replaySessionId;
        }
      } catch {
        /* never block a Tax API call on replay id resolution */
      }
    }

    const controller = new AbortController();
    let timedOut = false;
    let rejectTimeout!: (reason: Error) => void;
    const timeoutPromise = new Promise<never>((_resolve, reject) => {
      rejectTimeout = reject;
    });
    let rejectCancellation!: (reason: Error) => void;
    const cancellationPromise = new Promise<never>((_resolve, reject) => {
      rejectCancellation = reject;
    });
    const timeoutId = setTimeout(() => {
      timedOut = true;
      const error = timeoutError(requestTimeoutMs);
      controller.abort();
      rejectTimeout(error);
    }, requestTimeoutMs);
    const forwardAbort = () => {
      const error = abortError(request.signal?.reason);
      controller.abort(request.signal?.reason);
      rejectCancellation(error);
    };

    if (request.signal) {
      request.signal.addEventListener("abort", forwardAbort, { once: true });
      if (request.signal.aborted) forwardAbort();
    }

    let response: Response;
    let body: unknown;
    const sentAt = now();
    try {
      response = await Promise.race([
        fetchImpl(`${options.baseUrl}${request.path}${buildQueryString(request.query)}`, {
          method: request.method,
          headers,
          body: request.body === undefined ? undefined : JSON.stringify(request.body),
          signal: controller.signal,
          credentials: "omit",
          mode: "cors",
          // Authenticated, live state (task status, progress): never answered from the
          // HTTP cache, so a poll always sees what the server has now.
          cache: "no-store",
          ...(request.keepalive ? { keepalive: true } : {}),
        }),
        timeoutPromise,
        cancellationPromise,
      ]);

      // A custom fetch implementation may resolve after ignoring AbortSignal;
      // keep the timeout contract true for adapters and tests as well as the
      // browser's native fetch.
      if (timedOut) {
        throw timeoutError(requestTimeoutMs);
      }

      // Error responses always use the JSON problem contract, even when the
      // successful response is a binary artifact. A raw body must never be
      // passed through JSON.parse; it is an authenticated byte stream.
      if (!response.ok || (request.responseType ?? "json") === "json") {
        const text = await Promise.race([response.text(), timeoutPromise, cancellationPromise]);
        body = safeJsonParse(text);
      } else {
        body = await Promise.race([response.arrayBuffer(), timeoutPromise, cancellationPromise]);
      }
      if (timedOut) {
        throw timeoutError(requestTimeoutMs);
      }
    } catch (cause) {
      if (request.signal?.aborted && !timedOut) {
        // Preserve cancellation for TanStack Query. It must not become a
        // retryable network failure when a screen unmounts or supersedes a
        // request.
        throw abortError(request.signal.reason);
      }
      if (!isReplayExemptPath(request.path)) {
        try {
          options.onNetworkFailure?.({
            requestId,
            method: request.method,
            path: request.path,
          });
        } catch {
          /* never block error mapping on replay signals */
        }
      }
      if (timedOut) {
        throw networkError(timeoutError(requestTimeoutMs), {
          method: request.method,
          path: request.path,
          timedOut: true,
        });
      }
      const offline = typeof navigator !== "undefined" && navigator.onLine === false;
      if (!offline && now() - sentAt >= GATEWAY_DEADLINE_HINT_MS) {
        throw gatewayDeadlineError(cause, { method: request.method, path: request.path });
      }
      throw networkError(cause, {
        method: request.method,
        path: request.path,
        offline,
      });
    } finally {
      clearTimeout(timeoutId);
      request.signal?.removeEventListener("abort", forwardAbort);
    }

    if (!response.ok) {
      // API problem mapping intentionally lives outside the network catch.
      // A 403/409/etc. is a domain response and must retain its contract.
      // A 401 with no bearer is an unauthenticated probe, not a revoked
      // session — signing out here would drop a tab that is still refreshing.
      if (response.status === 401 && token) options.onUnauthenticated?.();
      const retryAfterHeader = response.headers?.get("Retry-After") ?? null;
      throw toTaxApiError(response.status, body, {
        method: request.method,
        path: request.path,
        retryAfterSeconds: parseRetryAfter(retryAfterHeader),
      });
    }

    if (
      (request.responseType ?? "json") === "json" &&
      typeof body === "string" &&
      body.trim() !== ""
    ) {
      // A 2xx that is not JSON (a captive portal, a proxy error page, the SPA
      // shell) must not reach a screen that expects a record.
      throw unexpectedResponseError(new Error("Non-JSON success body"), {
        method: request.method,
        path: request.path,
        status: response.status,
      });
    }

    return { status: response.status, body };
  };
}

function timeoutError(timeoutMs: number): Error {
  const error = new Error(`Tax API request timed out after ${timeoutMs}ms`);
  error.name = "TimeoutError";
  return error;
}

function abortError(reason: unknown): Error {
  if (reason instanceof Error && reason.name === "AbortError") return reason;
  if (typeof DOMException !== "undefined") {
    return new DOMException("The request was aborted", "AbortError");
  }
  const error = new Error("The request was aborted");
  error.name = "AbortError";
  return error;
}

function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}
